import { useEffect, useId, useState } from "react";
import { Sparkles } from "lucide-react";

import { cn } from "@/lib";
import { Button, Loading } from "@/components/ui";
import { PublicTiersAPI, type PublicTier } from "@/services/public-tiers";
import { BillingAPI, type GrowthBillingState } from "@/services/billing";

/**
 * Paywall for a clinic without an active subscription. Growth is the single
 * CliniSharp plan and is sales-led (owner decision 2026-10-20): there is no
 * plan choice and no card form. The screen shows the two charges, their
 * amounts from the public tiers endpoint, their state, and the Asaas payment
 * links once the CliniSharp team has issued them after the diagnosis.
 */

type Loaded = { tier: PublicTier | null; state: GrowthBillingState };

function formatBrl(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function amountLabel(value: number | undefined): string {
  return value && value > 0 ? formatBrl(value) : "Valor definido após o diagnóstico";
}

interface ChargeCardProps {
  title: string;
  amount: string;
  cadence: string;
  status: string;
  settled: boolean;
  paymentLink: string | null;
  payLabel: string;
}

function ChargeCard({ title, amount, cadence, status, settled, paymentLink, payLabel }: ChargeCardProps) {
  const titleId = useId();
  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-4 rounded-2xl border border-app-border bg-white p-6"
    >
      <div className="flex items-start justify-between gap-3">
        <h2 id={titleId} className="text-lg font-semibold text-foreground">
          {title}
        </h2>
        <span
          className={cn(
            "rounded-full px-2.5 py-1 text-xs font-medium",
            settled ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
          )}
        >
          {status}
        </span>
      </div>
      <div>
        <p className="text-2xl font-bold text-foreground">{amount}</p>
        <p className="text-sm text-muted-foreground">{cadence}</p>
      </div>
      {paymentLink && (
        <Button asChild variant="petrol">
          <a href={paymentLink} rel="noopener noreferrer">
            {payLabel}
          </a>
        </Button>
      )}
    </section>
  );
}

export function OnboardingBilling() {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([PublicTiersAPI.list(), BillingAPI.getGrowthState()])
      .then(([tiers, state]) => {
        if (cancelled) return;
        setLoaded({ tier: tiers.find((tier) => tier.key === "growth") ?? null, state });
      })
      .catch(() => {
        if (!cancelled) setError("Não foi possível carregar o seu plano. Tente novamente.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activation = loaded?.state.activation ?? null;
  const subscription = loaded?.state.subscription ?? null;
  const activationPaid = activation?.status === "paid";
  const subscriptionActive = subscription?.status === "active";
  // Each charge speaks for itself; the preparation message belongs to the
  // Ativação alone, and only while the CliniSharp team has not issued it.
  const activationStatus = activation === null ? "Não emitida" : activationPaid ? "Paga" : "Pendente";
  const subscriptionStatus = subscription === null ? "Não emitida" : subscriptionActive ? "Ativa" : "Aguardando ativação";
  const awaitingDiagnosis = activation === null;

  return (
    <div className="min-h-screen w-full bg-zinc-50">
      <div className="container mx-auto max-w-3xl px-4 py-12">
        <header className="mb-10 space-y-4 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1.5 text-sm font-medium text-teal-700">
            <Sparkles className="h-4 w-4" />
            Plano único da CliniSharp
          </div>
          <h1 className="text-3xl font-bold text-foreground md:text-4xl">Plano Growth</h1>
          <p className="mx-auto max-w-2xl text-muted-foreground">
            O Growth tem duas cobranças: a Ativação, paga uma vez na implantação, e a Evolução
            contínua, a assinatura mensal.
          </p>
        </header>

        {error && (
          <div className="mx-auto max-w-md rounded-lg border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700">
            {error}
          </div>
        )}

        {!error && loaded === null && (
          <div className="flex items-center justify-center py-24">
            <Loading />
          </div>
        )}

        {loaded && (
          <>
            <div className="mb-8 grid grid-cols-1 gap-5 md:grid-cols-2">
              <ChargeCard
                title="Ativação"
                amount={amountLabel(loaded.tier?.activation_price_brl)}
                cadence="Pagamento único"
                status={activationStatus}
                settled={activationPaid}
                paymentLink={activation?.payment_link ?? null}
                payLabel="Pagar Ativação"
              />
              <ChargeCard
                title="Evolução contínua"
                amount={amountLabel(loaded.tier?.monthly_price_brl)}
                cadence="por mês"
                status={subscriptionStatus}
                settled={subscriptionActive}
                paymentLink={subscription?.payment_link ?? null}
                payLabel="Pagar Evolução contínua"
              />
            </div>

            {awaitingDiagnosis && (
              <p className="rounded-lg border border-app-border bg-white p-4 text-center text-sm text-muted-foreground">
                Sua Ativação está sendo preparada pela CliniSharp. Você receberá o link de pagamento
                após o diagnóstico.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
